import { DateTime, Settings } from 'luxon';

/*
 * Central collection of commonly used date formats in accordance to the TUIC guidelines and NOT to ISO-8601.
 *
 * Resources on the ISO-8601 as reference for comparison:
 * - https://en.wikipedia.org/wiki/List_of_date_formats_by_country
 * - https://en.wikipedia.org/wiki/Date_and_time_notation_in_the_United_Kingdom
 * - https://moment.github.io/luxon/#/formatting
 *
 * Ergänzung:
 * Nach dem TUIC Language Guide wird unabhängig von der ausgewählten Sprache
 * grundsätzlich der Punkt als Datumstrenner verwendet.
 */

const getValidLocale = (locale: undefined | string) => {
  if (locale === 'en') {
    return 'en-GB';
  }
  return 'de';
};

const getValidDate = (
  date: undefined | Date | DateTime | string,
  locale: undefined | string = 'de',
): undefined | DateTime => {
  if (!date) {
    return undefined;
  }

  const newDate =
    date instanceof DateTime
      ? date
      : date instanceof Date
        ? DateTime.fromJSDate(date)
        : DateTime.fromISO(date);

  if (!newDate?.isValid) {
    return undefined;
  }
  return newDate.setLocale(getValidLocale(locale));
};

/**
 * Returns a rezoned datetime instance.
 */
export const getRezonedDateTime = (dateTime: DateTime | Date | string) => {
  const datetime =
    typeof dateTime === 'string'
      ? DateTime.fromISO(dateTime, {
          setZone: true,
        })
      : dateTime instanceof Date
        ? DateTime.fromJSDate(dateTime)
        : dateTime;

  return datetime.setZone(Settings.defaultZone, {
    keepLocalTime: true,
  });
};

const monthNamesShort_enGB: { [key: number]: string } = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

/**
 * European Standard format:  09.09.2024   or shortened as   09.09.
 */
export const formatDateEuropeanStandard = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
  short: boolean = false,
): string => {
  const validDate = getValidDate(date, locale);
  return (
    (short
      ? validDate?.toFormat('dd.MM.')
      : validDate?.toFormat('dd.MM.yyyy')) ?? ''
  );
};

/**
 * Date with full weekday and standard format:
 * i.e. for 'de':     Donnerstag, 20.03.2025
 * i.e. for 'en-GB':  Thursday, 20.03.2025
 */
export const formatDateWithFullWeekday = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
): string => {
  if (!date) {
    return '';
  }
  const validDate = getValidDate(date, locale);

  if (!validDate) {
    return '';
  }

  const weekday = validDate.toLocaleString({ weekday: 'long' });

  return `${weekday}, ${validDate.toFormat('dd.MM.yyyy')}`;
};

/**
 * Date with written month, shortened:
 * i.e. for 'de':     27. Sept. 2024
 * i.e. for 'en-GB':  27 Sep 2024
 */
export const formatDateWithWrittenMonthShortened = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
): string => {
  const validDate = getValidDate(date, locale);

  if (locale.startsWith('en')) {
    const { day, month, year } = validDate ?? {};

    if (day && month && year) {
      return `${day} ${monthNamesShort_enGB[month]} ${year}`;
    }
  }

  return (
    validDate?.toLocaleString({
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) ?? ''
  );
};

/**
 * Date with weekday, shortened:
 * i.e. for 'de':     Fr., 30. Sep.
 * i.e. for 'en-GB':  Fr, 30 Sep
 */
export const formatDateWithWeekdayShortened = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
): string => {
  const validDate = getValidDate(date, locale);

  if (locale.startsWith('en')) {
    const { day, month } = validDate ?? {};
    const weekday = validDate?.toLocaleString({ weekday: 'short' });

    if (weekday && day && month) {
      return `${weekday}, ${day} ${monthNamesShort_enGB[month]}`;
    }
  }

  return (
    validDate?.toLocaleString({
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    }) ?? ''
  );
};

/**
 * Date with weekday and year, shortened:
 * i.e. for 'de':     Fr., 30. Aug. 2024
 * i.e. for 'en-GB':  Fr, 30 Aug 2024
 */
export const formatDateWithWeekdayAndYearShortened = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
): string => {
  const validDate = getValidDate(date, locale);

  if (locale.startsWith('en')) {
    const { day, month, year } = validDate ?? {};
    const weekday = validDate?.toLocaleString({ weekday: 'short' });

    if (weekday && day && month && year) {
      return `${weekday}, ${day} ${monthNamesShort_enGB[month]} ${year}`;
    }
  }

  return (
    validDate?.toLocaleString({
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) ?? ''
  );
};

/**
 * Date with weekday.
 * Expects a DateTime date with locale already set.
 *
 * i.e. for 'de':    Fr, 30.08.24   or   Fr, 30.08.
 * i.e. for 'en-GB': Fri, 30.08.24  or   Fri, 30.08.
 */
const formatDateWithWeekday = (
  date: DateTime | undefined,
  withoutYear: boolean = false,
): string => {
  const weekday = date?.toFormat('ccc');
  const day = date?.toFormat(withoutYear ? 'dd.MM.' : 'dd.MM.yy');

  return `${weekday}, ${day}`;
};

/**
 * Formats two dates with weekday, considering if they are in the same year.
 *
 * i.e. for 'de':
 *   { startDateExpression: 'Fr, 30.08.',  endDateExpression: 'Sa, 31.08.24' }
 *   { startDateExpression: 'Fr, 30.12.22, endDateExpression: 'So, 01.01.23' }
 *
 * i.e. for 'en-GB':
 *   { startDateExpression: 'Fri, 30.08.',   endDateExpression: 'Sat, 31.08.24' }
 *   { startDateExpression: 'Fri, 30.12.22', endDateExpression: 'Sun, 01.01.23' }
 */
export const formatTwoDatesWithWeekday = (
  startDate: Date | DateTime | string | undefined,
  endDate: Date | DateTime | string | undefined,
  locale: string,
) => {
  const startDateTime = getValidDate(startDate, locale);
  const endDateTime = getValidDate(endDate, locale);
  const isSameYear = startDateTime?.year === endDateTime?.year;

  return {
    startDateExpression: formatDateWithWeekday(startDateTime, isSameYear) ?? '',
    endDateExpression: formatDateWithWeekday(endDateTime) ?? '',
  };
};

/*
 * ISO standard dates
 */

/**
 * European Standard format:
 * i.e. for 'de':     09.09.2024   or shortened as   09.09.
 * i.e. for 'en-GB':  09/09/2024   or shortened as   09/09
 */
export const formatDateEuropeanStandardISO8601 = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
  short: boolean = false,
): string => {
  return (
    getValidDate(date, locale)?.toLocaleString({
      day: '2-digit',
      month: '2-digit',
      year: short ? undefined : 'numeric',
    }) ?? ''
  );
};

/**
 * Date with weekday, shortened:
 * i.e. for 'de':     Fr., 30. Aug.
 * i.e. for 'en-GB':  Fr, 30 Aug
 */
export const formatDateWithWeekdayShortenedISO8601 = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
): string => {
  return (
    getValidDate(date, locale)?.toLocaleString({
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    }) ?? ''
  );
};

/**
 * Date with weekday and year, shortened:
 * i.e. for 'de':     Fr., 30. Aug. 2024
 * i.e. for 'en-GB':  Fr, 30 Aug 2024
 */
export const formatDateWithWeekdayAndYearShortenedISO8601 = (
  date: undefined | Date | DateTime,
  locale: undefined | string = 'de',
): string => {
  return (
    getValidDate(date, locale)?.toLocaleString({
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) ?? ''
  );
};

export type DateSegment = 'day' | 'hour' | 'minute' | 'second';
export function getDateSegmentDifference(
  date: Date,
  diffType: DateSegment,
): number {
  const now = new Date();
  const diffInMs = date.getTime() - now.getTime();

  switch (diffType) {
    case 'day':
      return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    case 'hour':
      return Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    case 'minute':
      return Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
    case 'second':
      return Math.floor((diffInMs % (1000 * 60)) / 1000);
    default:
      return 0;
  }
}

/**
 * Simple helper to convert a date into the 'YYYY-MM-DD' string format.
 * @param date
 * @param locale
 */
export function dateToYearMonthDay(date: Date, locale: string = 'de'): string {
  const fromYear = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(
    date,
  );
  const fromMonth = new Intl.DateTimeFormat(locale, {
    month: '2-digit',
  }).format(date);
  const fromDay = new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(
    date,
  );
  return `${fromYear}-${fromMonth}-${fromDay}`;
}
